import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquareText, Loader2, Trash2, ChevronRight } from 'lucide-react';
import { API_PATH, request } from '../config/api';
import { useConfirm } from '../store/ConfirmDialogContext';
import { formatDateTime } from '../utils/formatDate';
import PageHeroHeader from '../components/ui/PageHeroHeader';
import styles from './ResponsesPage.module.css';

// Every activity type shapes its answer differently — this makes a best-effort readable
// string out of whichever fields are present, falling back to raw JSON for anything unusual
// (drawings, click coordinates, pair matches, etc.) rather than trying to special-case all 25.
function formatAnswer(raw) {
    let answer = raw;
    if (typeof answer === 'string') {
        try { answer = JSON.parse(answer); } catch { return answer || '—'; }
    }
    if (answer == null) return '—';
    if (typeof answer !== 'object') return String(answer);
    if (typeof answer.text === 'string' && answer.text.trim()) return answer.text;
    if (typeof answer.outcome === 'string') return answer.outcome;
    if (typeof answer.answer === 'string') return answer.answer;
    if (typeof answer.prediction === 'string') return answer.prediction;
    if (typeof answer.reaction === 'string') return answer.reaction;
    if (typeof answer.side === 'string') return answer.side;
    if (typeof answer.hotspot === 'string') return answer.hotspot;
    if (typeof answer.guess !== 'undefined') return String(answer.guess);
    if (typeof answer.value !== 'undefined') return String(answer.value);
    if (Array.isArray(answer.selectedOptions) && answer.selectedOptions.length) return answer.selectedOptions.join(', ');
    if (Array.isArray(answer.words) && answer.words.length) return answer.words.join(', ');
    if (Array.isArray(answer.order) && answer.order.length) return answer.order.join(' → ');
    try {
        return JSON.stringify(answer);
    } catch {
        return String(answer);
    }
}

const ResponsesPage = () => {
    const confirm = useConfirm();
    const [events, setEvents] = useState([]);
    const [activities, setActivities] = useState([]);
    const [responses, setResponses] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [selectedActivityId, setSelectedActivityId] = useState('');
    const [loadingEvents, setLoadingEvents] = useState(true);
    const [loadingActivities, setLoadingActivities] = useState(false);
    const [loadingResponses, setLoadingResponses] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        setLoadingEvents(true);
        request(`${API_PATH}/events`)
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load events'))))
            .then((data) => setEvents(Array.isArray(data) ? data : []))
            .catch(() => setError('Failed to load your events. Try refreshing.'))
            .finally(() => setLoadingEvents(false));
    }, []);

    useEffect(() => {
        setSelectedActivityId('');
        setResponses([]);
        if (!selectedEventId) { setActivities([]); return; }
        setLoadingActivities(true);
        setError('');
        request(`${API_PATH}/events/${selectedEventId}/activities`)
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load polls'))))
            .then((data) => setActivities(Array.isArray(data) ? data : []))
            .catch(() => setError('Failed to load this event’s polls.'))
            .finally(() => setLoadingActivities(false));
    }, [selectedEventId]);

    const fetchResponses = useCallback(() => {
        if (!selectedEventId || !selectedActivityId) return;
        setLoadingResponses(true);
        setError('');
        request(`${API_PATH}/events/${selectedEventId}/activities/${selectedActivityId}/results`)
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load responses'))))
            .then((data) => setResponses(Array.isArray(data) ? data : []))
            .catch(() => setError('Failed to load responses for this poll.'))
            .finally(() => setLoadingResponses(false));
    }, [selectedEventId, selectedActivityId]);

    useEffect(() => { fetchResponses(); }, [fetchResponses]);

    const handleDelete = async (responseId, participant) => {
        const ok = await confirm({
            title: 'Delete Response',
            message: `Delete ${participant ? `${participant}'s` : 'this'} response? This cannot be undone.`,
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setDeletingId(responseId);
        try {
            const res = await request(
                `${API_PATH}/events/${selectedEventId}/activities/${selectedActivityId}/results/${responseId}`,
                { method: 'DELETE' }
            );
            if (res.ok) {
                setResponses((prev) => prev.filter((r) => r.id !== responseId));
                setActivities((prev) => prev.map((a) => (
                    String(a.id) === String(selectedActivityId)
                        ? { ...a, response_count: Math.max(0, (a.response_count || 0) - 1) }
                        : a
                )));
            } else {
                const data = await res.json().catch(() => ({}));
                alert(data.error || 'Failed to delete response.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to delete response.');
        } finally {
            setDeletingId(null);
        }
    };

    const selectedActivity = activities.find((a) => String(a.id) === String(selectedActivityId));

    return (
        <div className={styles.page}>
            <PageHeroHeader
                icon={MessageSquareText}
                title="Manage Responses"
                subtitle="Pick an event and a poll to see every response, and delete any individual one you need to."
            />

            <div className={styles.pickerRow}>
                <div className={styles.pickerField}>
                    <label className={styles.pickerLabel} htmlFor="responses-event-select">Event</label>
                    <select
                        id="responses-event-select"
                        className={styles.select}
                        value={selectedEventId}
                        onChange={(e) => setSelectedEventId(e.target.value)}
                        disabled={loadingEvents}
                    >
                        <option value="">{loadingEvents ? 'Loading…' : 'Select an event'}</option>
                        {events.map((ev) => (
                            <option key={ev.id} value={ev.id}>{ev.name} ({ev.code})</option>
                        ))}
                    </select>
                </div>
                <ChevronRight size={18} className={styles.pickerArrow} aria-hidden />
                <div className={styles.pickerField}>
                    <label className={styles.pickerLabel} htmlFor="responses-poll-select">Poll</label>
                    <select
                        id="responses-poll-select"
                        className={styles.select}
                        value={selectedActivityId}
                        onChange={(e) => setSelectedActivityId(e.target.value)}
                        disabled={!selectedEventId || loadingActivities}
                    >
                        <option value="">
                            {!selectedEventId ? 'Select an event first' : loadingActivities ? 'Loading…' : 'Select a poll'}
                        </option>
                        {activities.map((a) => (
                            <option key={a.id} value={a.id}>
                                {a.title || a.type} ({a.response_count || 0} response{a.response_count === 1 ? '' : 's'})
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {error && <div className={styles.errorBanner} role="alert">{error}</div>}

            {selectedActivityId && (
                <section className={styles.responsesSection}>
                    <div className={styles.responsesHeader}>
                        <h2 className={styles.responsesTitle}>{selectedActivity?.title || 'Responses'}</h2>
                        <span className={styles.responsesCount}>
                            {responses.length} response{responses.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    {loadingResponses ? (
                        <div className={styles.loading}>
                            <Loader2 size={24} className={styles.spinner} />
                            <span>Loading responses…</span>
                        </div>
                    ) : responses.length === 0 ? (
                        <p className={styles.empty}>No responses yet for this poll.</p>
                    ) : (
                        <ul className={styles.responseList}>
                            {responses.map((r) => (
                                <li key={r.id} className={styles.responseRow}>
                                    <div className={styles.responseMain}>
                                        <span className={styles.responseParticipant}>{r.participant || 'Anonymous'}</span>
                                        <span className={styles.responseAnswer}>{formatAnswer(r.answer)}</span>
                                        <span className={styles.responseDate}>{formatDateTime(r.created_at) || '—'}</span>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.deleteBtn}
                                        onClick={() => handleDelete(r.id, r.participant)}
                                        disabled={deletingId === r.id}
                                        aria-label={`Delete ${r.participant || 'this'}'s response`}
                                    >
                                        {deletingId === r.id ? <Loader2 size={14} className={styles.spin} /> : <Trash2 size={14} />}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            )}
        </div>
    );
};

export default ResponsesPage;
